import type { ProductGalleryExtended } from '#root/api/clients/product/data-contracts'
import type { OptimizelyGalleryExperimentVariables } from '#types/featureFlags'
import type { MaybeRefOrGetter } from 'vue'
import { GALLERY_CONTEXT_CODES, isGenderedGalleryKey } from '#commerce/utils/gallery/getGalleryContextKey'
import { getOptimizelyCmabStaticExperiments } from '#core/utils/getOptimizelyCmabAttributes'

interface ActiveGallerySource {
  id?: string
  gallery?: ProductGalleryExtended
  altGalleries?: Record<string, ProductGalleryExtended>
}

interface ResolvedGallery {
  gallery: ProductGalleryExtended
  /** The `altGalleries` key that was selected; undefined when the primary gallery is used. */
  key?: string
}

const parseIncludedProductIds = (value?: string) =>
  (value ?? '').split(',').map((id) => id.trim().toUpperCase()).filter(Boolean)

/**
 * Resolves the active product media gallery from the highest-priority source.
 *
 * Sources, in precedence order: the `?altGallery` Optimizely debug override; the
 * `?gc` opaque context code; an assigned Optimizely gallery experiment; the
 * supplied `contextKey` (e.g. a PLP gender context); then the primary `gallery`.
 * The `?gc` code and `contextKey` paths are independent of Optimizely and resolve
 * even when no experiment is running (or when Optimizely is disabled entirely).
 *
 * Gallery experiment flags come from `decideAll()` plus individual decisions
 * for blob-listed CMAB flags. Any flag with a `useAltGallery` variable is a
 * gallery experiment. Brands add new experiments directly in Optimizely without
 * any frontend code or config change. A product is enrolled when its `id`
 * appears in the flag's `includedProductIds`; the assigned variation turns
 * `useAltGallery` on and names the gallery via `galleryName`. Control, a
 * disabled experiment, an untargeted product, or a failed assignment all fall
 * back to the primary `gallery`. `?altGallery=<name>` forces a named gallery
 * and `?altGallery=default` forces the primary gallery for Optimizely
 * debugging. `?gc=<code>` manually supplies a gallery-context key via the opaque
 * codes in `getGalleryContextKey.ts` (e.g. `m` for `mens`), falling back to the
 * primary gallery when the code is unmapped or has no alternate gallery.
 *
 * Works for both the PDP (pass the `Product`, whose galleries live at the root)
 * and product cards (pass a computed exposing `id`, `gallery`, `altGalleries`).
 *
 * @param source - Ref/getter/value exposing `id`, `gallery`, and optional `altGalleries`.
 * @param contextKey - Optional key selecting a named `altGalleries` entry (e.g. `"mens"`, `"womens"`);
 *   used as a fallback when no `?gc` code or Optimizely variant is assigned.
 * @returns `activeGallery` — the media array for the resolved gallery, falling
 * back to the primary `gallery`; `galleryPending` — `true` while an enrolled
 * product still awaits the client-side decisions to settle; and `isGenderedGallery` —
 * `true` when the resolved gallery is a gendered `altGalleries` entry (`"mens"`,
 * `"womens"`, `"girls"`, `"boys"`).
 * @category Composables
 */

export const useActiveGallery = (
  source: MaybeRefOrGetter<ActiveGallerySource | null | undefined>,
  contextKey?: MaybeRefOrGetter<string | undefined>
) => {
  const { $feature, $gtm } = useNuxtApp()
  const route = useRoute()

  // `?gc` overrides the gallery via an opaque code, independent of Optimizely —
  // it applies the same way whether or not an experiment is running.
  const rawGalleryContextCode = computed(() => {
    const value = route.query.gc
    return typeof value === 'string' ? value : undefined
  })

  // Resolves `altGalleries[fallbackKey]`, unless `?gc` is present, in which case it
  // takes precedence (falling back to the primary gallery when the code is unmapped).
  // `key` is only set when an alternate gallery was actually selected.
  const resolveGallery = (
    altGalleries: Record<string, ProductGalleryExtended> | undefined,
    gallery: ProductGalleryExtended,
    fallbackKey: string | undefined
  ): ResolvedGallery => {
    const code = rawGalleryContextCode.value
    const key = code === undefined ? fallbackKey : GALLERY_CONTEXT_CODES[code.toLowerCase()]
    const alt = key ? altGalleries?.[key] : undefined
    return alt ? { gallery: alt, key } : { gallery }
  }

  if (!$feature.configP13n?.enableOptimizely) {
    const resolved = computed(() => {
      const { altGalleries, gallery = [] } = toValue(source) ?? {}
      return resolveGallery(altGalleries, gallery, toValue(contextKey))
    })
    const activeGallery = computed<ProductGalleryExtended>(() => resolved.value.gallery)
    const isGenderedGallery = computed(() => isGenderedGalleryKey(resolved.value.key))
    const galleryPending = computed(() => false)

    return { activeGallery, galleryPending, isGenderedGallery }
  }

  const { decisions, decide, decideAll, allDecisionsResolved } = useOptimizely()
  const isMounted = useMounted()
  let isDecisionTracked = false

  const cmabKeys = import.meta.client
    ? [...new Set(getOptimizelyCmabStaticExperiments(window.vfaConfig?.optimizelyCmabMap))]
    : []
  const decisionStatus = useState<'idle' | 'loading' | 'ready'>('p13n-gallery-decision-status', () => 'idle')

  if (import.meta.client && decisionStatus.value === 'idle') {
    decisionStatus.value = 'loading'

    const loadDecisions = async () => {
      try {
        if (!allDecisionsResolved.value) await decideAll()
      }
      catch (err) {
        log.warn(`[GalleryExperiment] decideAll failed: ${String(err)}`)
      }

      await Promise.all(cmabKeys.map((key) =>
        decide(key, { disableDecisionEvent: true })
          .catch((err) => log.warn(`[GalleryExperiment] decide failed for ${key}: ${String(err)}`))
      ))
      decisionStatus.value = 'ready'
    }

    loadDecisions()
  }

  // Find the gallery assigned by the first flag decision whose variables identify
  // as a gallery experiment (has `useAltGallery`) and targets this product.
  const assignedGalleryName = computed<string | undefined>(() => {
    const { id } = toValue(source) ?? {}
    if (!id) return undefined
    const productId = id.toUpperCase()

    for (const [key, decision] of decisions.value) {
      if (decisionStatus.value === 'loading' && cmabKeys.includes(key)) continue
      if (!decision) continue

      const { galleryName, includedProductIds, useAltGallery }
        = (decision.variables as Partial<OptimizelyGalleryExperimentVariables>) ?? {}
      if (!useAltGallery || !galleryName || !parseIncludedProductIds(includedProductIds).includes(productId)) continue

      // `decide` writes `decisions`, which re-runs this computed
      if (!isDecisionTracked) {
        isDecisionTracked = true
        decide(decision.flagKey ?? '')
        $gtm.push('optimizely.onOptimizelyDecision', {
          flagKey: decision.flagKey,
          ruleKey: decision.ruleKey,
          variationKey: decision.variationKey,
          contentId: null
        })
      }
      if (galleryName !== 'default') return galleryName
    }
    return undefined
  })

  const resolved = computed<ResolvedGallery>(() => {
    const { altGalleries, gallery = [] } = toValue(source) ?? {}

    // `altGallery` remains reserved for Optimizely experiment debugging.
    const queryKey = route.query.altGallery as string | undefined
    if (queryKey) {
      const alt = queryKey === 'default' ? undefined : altGalleries?.[queryKey]
      return alt ? { gallery: alt, key: queryKey } : { gallery }
    }

    return resolveGallery(altGalleries, gallery, assignedGalleryName.value || toValue(contextKey))
  })
  const activeGallery = computed<ProductGalleryExtended>(() => resolved.value.gallery)
  const isGenderedGallery = computed(() => isGenderedGalleryKey(resolved.value.key))

  const galleryPending = computed(() => {
    if (route.query.altGallery || rawGalleryContextCode.value !== undefined) return false

    const { altGalleries } = toValue(source) ?? {}
    if (!altGalleries || !Object.keys(altGalleries).length) return false

    // gender context is synchronous — skip Optimizely wait when it already resolves the gallery
    const key = toValue(contextKey)
    if (key && altGalleries[key]) return false

    return !isMounted.value || !allDecisionsResolved.value || (cmabKeys.length > 0 && decisionStatus.value !== 'ready')
  })

  return { activeGallery, galleryPending, isGenderedGallery }
}
